import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import {collection, getDocs, addDoc, doc, updateDoc } from 'firebase/firestore';
import { buildMyClassesQuery, getDocsByClassIds } from '../utils/classroomQueries';
import { fireConfetti } from '../utils/celebrate';
import { logAudit, logError } from '../services/Logger';
import { sortByLastName } from '../utils/sortByLastName';
import useFitText from '../hooks/useFitText';
import { TEST_CATEGORY_MAP } from '../constants/assessmentTypes';
import {
  VOWEL_ITEMS,
  VOWEL_PROMPTS,
  VOWEL_DISCONTINUE_RULE,
  RESP_NOT_ADMINISTERED,
  isVowelTestType,
  vowelLetters,
  vowelItemKey,
} from '../constants/vowelTests';
import { getRunOrder } from '../utils/letterOrders';
import { isPlainLetterKeyEvent, classifyLetterKeyResponse } from '../utils/incorrectResponses';
import { isFeatureEnabled } from '../constants/featureFlags';
import { isDocInSchoolYear, schoolYearStartYear } from '../utils/schoolYear';
import { resolveActiveSchoolYear, getCalendarQuarters } from '../utils/activeSchoolYear';
import { usePageLoad } from '../hooks/usePageLoad';
import LoadErrorNotice from '../components/LoadErrorNotice';
import SchoolYearNotice from '../components/SchoolYearNotice';
import NoRosterNotice from '../components/NoRosterNotice';
import AssessmentResponseControls from '../components/AssessmentResponseControls';
import { trackWrite } from '../utils/pendingWrites';
import { classLabelFromId } from '../utils/classDisplay';
import { useOnboardingScreen, HINT_KEYS } from '../onboarding';
import {
  letterSoundTestFromSearch,
  DEFAULT_LETTER_SOUND_TEST,
  SOUNDS_VARIANTS_FLAG,
  letterSoundRows,
  runSpecForKey,
  glyphForCase,
  caseTagText,
  caseTagTitle,
  vowelCardCase,
  sizingDeckForCase,
} from '../utils/assessmentEntry';

/** The literacy card face (src/fonts) — a choice in the Assessment Font list, flag-gated. */
const LETTER_CARD_FONT = 'Letter Card Sans';

function AssessmentForm({ user }) {
  const location = useLocation();
  // Take Assessment handoff (v3.84.10): the Dashboard's selected Test Type
  // rides in `?testType=` (assessmentEntry.js). null = no key, an unknown
  // key, or a vowel key while the switch is off — the default runs, no
  // crash. Student and class keep arriving in location.state as before.
  // v3.85.0: the key is a ROW of the Letter/Sound table (a Sounds variant
  // key such as sounds_consonants included); a hidden / switched-off Sounds
  // variant arrives already resolved to Sounds – Uppercase.
  const requestedTest = letterSoundTestFromSearch(location.search);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedTest, setSelectedTest] = useState(requestedTest || DEFAULT_LETTER_SOUND_TEST);
  const [currentLetterIndex, setCurrentLetterIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [incorrectResponses, setIncorrectResponses] = useState({});
  const [letterFluency, setLetterFluency] = useState({});
  const [assessmentStartTime, setAssessmentStartTime] = useState(null);
  const [letterStartTime, setLetterStartTime] = useState(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  // Vowel tests (v3.73.0): the teacher script lives behind a "Hint" control,
  // closed by default. Once opened it stays open across that subtest's five
  // cards until closed; it closes again when the next subtest starts
  // (handleTestChange). No persistence, no setting.
  const [hintOpen, setHintOpen] = useState(false);
  const [quarters, setQuarters] = useState({});
  const [schoolYearInfo, setSchoolYearInfo] = useState(null);
  // The resolved active school-year start year, readable inside fetch closures.
  const activeYearRef = React.useRef(null);
  const [selectedFont, setSelectedFont] = useState('Teachers');
  const [fontSizePercentage, setFontSizePercentage] = useState(user?.profile?.fontSizePercentage || 100);
  
  const [deviceConfig, setDeviceConfig] = useState({
    cardWidth: '90%',
    cardHeight: '50vh'
  });

  // The rows the picker offers (src/utils/assessmentEntry.js — the one table
  // the Dashboard reads too) and what each RUNS: doc testType, glyph case and
  // the letters preset as not administered.
  const variantsOn = isFeatureEnabled(SOUNDS_VARIANTS_FLAG);
  const pickerRows = letterSoundRows();
  const runSpec = runSpecForKey(selectedTest);
  const docTestType = runSpec.testType;

  // Per-row letter lists. Letter rows: the teacher's per-test-type order
  // (v3.4.0, legacy customLetterOrder fallback — src/utils/letterOrders.js)
  // minus the row's preset (consonants only → 21 letters, never a stored
  // 21-letter order). Every Sounds row keys its responses by the same
  // uppercase letters; the case is drawn, not stored. Vowel rows (v3.73.0):
  // five fixed lowercase glyphs each. Absent when their switch is off.
  const testLetters = {};
  pickerRows.forEach((row) => {
    testLetters[row.key] = isVowelTestType(row.testType)
      ? vowelLetters(row.testType)
      : getRunOrder(user?.profile, row);
  });
  if (!testLetters[selectedTest]) testLetters[selectedTest] = isVowelTestType(docTestType) ? vowelLetters(docTestType) : getRunOrder(user?.profile, runSpec);

  const isVowelTest = isVowelTestType(docTestType);
  // Response-map key for the item shown. Letter tests key by the glyph itself
  // (unchanged); vowel tests key by the item id ('a' on Long Vowels → 'a_long').
  const itemKeyFor = (letter) => (isVowelTest ? vowelItemKey(docTestType, letter) : letter);
  const currentVowelItem = isVowelTest
    ? (VOWEL_ITEMS[docTestType] || [])[currentLetterIndex] || null
    : null;

  // The case the card DRAWS. Sounds rows carry their own; the vowel cards are
  // always lowercase (v3.86.2 — they no longer follow the last Sounds pick);
  // the uppercase / lowercase tests are their own case. Display only: the
  // item letter, its id, the key handler and storage keep the bare letter.
  const resolvedCase = isVowelTest ? vowelCardCase() : (runSpec.letterCase || 'uppercase');
  const showsBothCases = resolvedCase === 'both';
  // Every card is drawn in the font picked under Assessment Font, whatever its
  // case (v3.86.4). v3.85.0–3.86.3 forced the literacy face on lowercase and
  // both-case glyphs and ignored the picker there; it is now one of the
  // picker's choices instead (offered only with the flag on).
  const cardFont = selectedFont;

  // What the STUDENT sees on the card: the item letter drawn in the resolved
  // case — the uppercase / lowercase tests show the letter as is, a Sounds
  // row its case ("m" / "M m"), the vowel cards lowercase ("a").
  const glyphFor = (letter) => (isVowelTest || docTestType === 'sounds' ? glyphForCase(letter, resolvedCase) : String(letter));
  const currentItemLetter = testLetters[selectedTest]?.[currentLetterIndex] || '';
  const currentDisplayLetter = currentItemLetter ? glyphFor(currentItemLetter) : '';
  // One size for every card of every Letter/Sound row of one case (v3.85.0
  // Letter Sizes fix, widened in v3.86.2): the card is fitted against the
  // whole alphabet in the run's case, not the run's own letters — so 'l', 'g'
  // and 'x' match, and the five vowel cards are the size and sit on the
  // baseline of the 26-letter runs. The word cards elsewhere keep the
  // per-item fit.
  const deck = sizingDeckForCase(resolvedCase);
  const { ref: fitTextRef, fontSize: dynamicFontSize, centerOffset, isCalculating } = useFitText(currentDisplayLetter, cardFont, 24, 600, { deck });

  useEffect(() => {
    document.title = 'Early Literacy Assessment - Assessment';
  }, []);

  /**
   * First load, in exactly three sequential round trips (2026-07 perf audit,
   * step 9 — the phone-on-school-wifi fix). It used to take five: the calendar
   * was fetched twice (resolveActiveSchoolYear, then a second hand-rolled
   * `quarters` + `districtCalendars` read), and the student chunks were awaited
   * one after another in a for-loop.
   *
   *   1. calendar → active school year   (session-cached in activeSchoolYear.js)
   *   2. my classes ∥ calendar quarters  (the calendar resolves from cache)
   *   3. students, all classId chunks issued in parallel
   *
   * Same data, same district-override precedence, same client-side school-year
   * filtering — only the number of hops changed.
   */
  const loadData = useCallback(async () => {
    if (!user) return;
    const districtId = user?.profile?.districtId || null;
    try {
      // Hop 1 — which school year is active? Calendar-driven (district first).
      const sy = await resolveActiveSchoolYear(districtId);
      setSchoolYearInfo(sy);
      activeYearRef.current = sy.status === 'active' ? sy.startYear : null;
      if (sy.status !== 'active') return;

      // Hop 2 — my classes. Role-aware query: pure-EA tokens use the
      // assistantEmails clause only (the or() shape is rules-denied without
      // the teacher claim). The calendar read alongside it is served from the
      // session cache hop 1 populated, so it costs no round trip and no reads.
      const [classesSnapshot, quartersData] = await Promise.all([
        getDocs(buildMyClassesQuery(user)),
        getCalendarQuarters(districtId),
      ]);
      setQuarters(quartersData);

      const classIds = classesSnapshot.docs
        .filter(d => !d.data().deleted && isDocInSchoolYear(d.data(), activeYearRef.current))
        .map(d => d.id);

      // Hop 3 — students for every class, chunks issued in parallel.
      const studentDocs = await getDocsByClassIds('students', classIds);
      const studentsList = studentDocs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => isDocInSchoolYear(s, activeYearRef.current));
      setStudents(studentsList);

      const uniqueClasses = [...new Set(studentsList.map(s => s.classId).filter(Boolean))];
      const originalClasses = uniqueClasses.map(classId => ({
        fullId: classId,
        originalId: classLabelFromId(classId)
      }));
      setClasses(originalClasses);

      const locationState = location.state || {};
      let studentIdFromUrl = locationState.studentId;
      let classIdFromUrl = locationState.classId;

      if (!studentIdFromUrl || !classIdFromUrl) {
        const queryParams = new URLSearchParams(location.search);
        if (!studentIdFromUrl) studentIdFromUrl = queryParams.get('studentId');
        if (!classIdFromUrl) classIdFromUrl = queryParams.get('classId');
        try { if (studentIdFromUrl) studentIdFromUrl = atob(studentIdFromUrl); } catch (e) { }
        try { if (classIdFromUrl) classIdFromUrl = atob(classIdFromUrl); } catch (e) { }
      }

      if (classIdFromUrl) {
        setSelectedClass(classIdFromUrl);
        if (studentIdFromUrl) {
          setSelectedStudent(studentIdFromUrl);
        }
      } else if (originalClasses.length > 0) {
        setSelectedClass(originalClasses[0].fullId);
      }
    } catch (err) {
      // Keep the server-side telemetry, then let usePageLoad turn this into a
      // visible, retryable error instead of an eternal "Loading data…".
      logError(user, 'DATA_FETCH_ERROR', err.message, err.stack, { context: 'AssessmentForm.fetchData' });
      throw err;
    }
  }, [user, location.search, location.state]);

  const { loading, error, retry } = usePageLoad(loadData, [user, location.search, location.state]);

  // Contextual hint readiness (v3.75.0, overlay only): a student is loaded on
  // a letter card. Inert unless the onboardingTour flag is on.
  useOnboardingScreen(HINT_KEYS.assessLetterSound, {
    ready: !!selectedStudent && !loading && currentLetterIndex < testLetters[selectedTest].length,
  });

  // Handle dynamic device sizing for the Flashcard UI
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      if (width < 768) {
        // Mobile
        setDeviceConfig({
          cardWidth: '90vw',
          cardHeight: `${Math.min(height * 0.5, 350)}px`
        });
      } else if (width >= 768 && width <= 1024) {
        // Tablet
        setDeviceConfig({
          cardWidth: '70vw',
          cardHeight: `${Math.min(height * 0.55, 450)}px`
        });
      } else {
        // Desktop
        setDeviceConfig({
          cardWidth: 'max(400px, 40vw)', // Keep it card-like but scale a bit
          cardHeight: 'min(450px, 50vh)'
        });
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initialize immediately
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filteredStudents = sortByLastName(students.filter(student => student.classId === selectedClass));

  const getCurrentQuarter = (date) => {
    const assessmentDate = date || new Date();
    const currentYear = assessmentDate.getFullYear();
    // Check current year and previous year (to handle school years starting in prev year)
    // For example, if today is Feb 2026, we want to check 2026 quarters AND 2025 quarters (since 2025 Q3 might be in 2026)
    const yearsToCheck = [currentYear.toString(), (currentYear - 1).toString()];

    for (const year of yearsToCheck) {
      const yearQuarters = quarters[year] || {};
      for (const quarter in yearQuarters) {
        const { start, end } = yearQuarters[quarter];
        // Create dates at midnight to avoid time issues, though simple comparison usually works
        // Assuming start/end strings are YYYY-MM-DD format from the input type="date"
        const startDate = new Date(start);
        const endDate = new Date(end);
        // inclusive check
        if (assessmentDate >= startDate && assessmentDate <= endDate) {
          return `${year}Q${quarter}`;
        }
      }
    }

    // Fallback: If no match found, default to finding the 'next' upcoming quarter or just the current year's first quarter
    const nextYear = (currentYear + 1).toString();
    const nextYearQuarters = quarters[nextYear] || {};
    if (nextYearQuarters['1']) {
      if (assessmentDate >= new Date(nextYearQuarters['1'].start)) {
        return `${nextYear}Q1`;
      }
    }
    // Final fallback uses the SCHOOL-year start year (July 1 – June 30 rule),
    // not the calendar year: a bare calendar year would file Jan–Jun
    // assessments into the NEXT school year's Q1.
    return `${activeYearRef.current ?? schoolYearStartYear(assessmentDate)}Q1`;
  };

  // Start timers when a student and test are selected and the first letter is ready
  useEffect(() => {
    if (selectedStudent && testLetters[selectedTest] && currentLetterIndex === 0) {
      const now = Date.now();
      if (!assessmentStartTime) setAssessmentStartTime(now);
      setLetterStartTime(now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent, selectedTest, currentLetterIndex]);

  const recordFluency = (letter) => {
    if (letterStartTime) {
      const timeTaken = Date.now() - letterStartTime;
      setLetterFluency(prev => ({ ...prev, [letter]: timeTaken }));
    }
  };

  // correct: boolean. heardLetter (optional): the letter the teacher pressed
  // to capture what the student actually said — only meaningful for incorrect
  // responses. A plain ← (or on-screen Incorrect) records no heard letter, and
  // re-answering a letter (via Back) always clears any previously captured one.
  const handleResponse = (correct, heardLetter = null) => {
    if (buttonsDisabled) return;

    const currentLetter = itemKeyFor(testLetters[selectedTest][currentLetterIndex]);
    recordFluency(currentLetter);

    setIncorrectResponses(prev => {
      const next = { ...prev };
      if (!correct && heardLetter) {
        next[currentLetter] = heardLetter;
      } else {
        delete next[currentLetter];
      }
      return next;
    });

    setResponses(prev => {
      const newResponses = { ...prev, [currentLetter]: correct ? 'Correct' : 'Incorrect' };
      if (currentLetterIndex === testLetters[selectedTest].length - 1) {
        setButtonsDisabled(true);
      }
      return newResponses;
    });
    
    if (currentLetterIndex < testLetters[selectedTest].length - 1) {
      setCurrentLetterIndex(currentLetterIndex + 1);
      setLetterStartTime(Date.now()); // Start timer for next letter
    } else {
      // Force index out of bounds to show the Completion screen
      setCurrentLetterIndex(testLetters[selectedTest].length);
    }
  };

  const handleBack = () => {
    if (currentLetterIndex > 0) {
      setCurrentLetterIndex(prev => prev - 1);
      setButtonsDisabled(false);

      // When going back, we restart the timer for the previous letter so we
      // accurately capture how long it took them *this time* before finalizing.
      setLetterStartTime(Date.now());
    }
  };

  // Vowel tests only (v3.73.0): the discontinue rule — "mark the rest not
  // administered". Every item from the current one onward is recorded as
  // NotAdministered (never Incorrect, never counted) and the run jumps to the
  // completion screen so the teacher can submit a partial administration.
  const handleMarkRestNotAdministered = (e) => {
    if (e?.target?.blur) e.target.blur();
    if (!isVowelTest || buttonsDisabled) return;
    const letters = testLetters[selectedTest];
    if (currentLetterIndex >= letters.length) return;
    setResponses(prev => {
      const next = { ...prev };
      for (let i = currentLetterIndex; i < letters.length; i += 1) {
        next[itemKeyFor(letters[i])] = RESP_NOT_ADMINISTERED;
      }
      return next;
    });
    setButtonsDisabled(true);
    setCurrentLetterIndex(letters.length);
  };

  // Keyboard Shortcuts for Assessment
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't interfere if the user is typing in a dropdown/input
      if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'SELECT') return;

      // Only allow shortcuts if we are currently assessing a student and buttons aren't disabled
      if (!selectedStudent || buttonsDisabled || currentLetterIndex >= testLetters[selectedTest].length) return;

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleResponse(true);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleResponse(false);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        handleBack();
      } else if (isPlainLetterKeyEvent(e)) {
        // Letter key = the teacher types WHAT THE STUDENT SAID (v3.27.0).
        // The same handler serves all five Letter/Sound tests — the vowel
        // tests included (v3.73.0): the pressed letter is the letter/sound
        // the student gave, recorded in the same incorrectResponses map.
        // If it matches the shown letter the response is Correct (v3.29.0);
        // otherwise Incorrect with the letter recorded. Plain ← stays
        // available for "incorrect, response not captured". Chorded keys
        // (Cmd/Ctrl/Alt) are left to the browser.
        const classified = classifyLetterKeyResponse(e.key, testLetters[selectedTest][currentLetterIndex], docTestType, runSpec.letterCase);
        if (classified) {
          e.preventDefault();
          handleResponse(classified.correct, classified.heard);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent, buttonsDisabled, currentLetterIndex, selectedTest, docTestType, runSpec.letterCase, testLetters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent) return;
    const currentLetters = testLetters[selectedTest];
    if (Object.keys(responses).length < currentLetters.length) {
      alert(`Please complete the assessment for all ${currentLetters.length} letters.`);
      return;
    }
    try {
      const quarter = getCurrentQuarter();
      const status = user?.role?.ea ? 'pending_review' : 'completed';
      
      const totalDurationMs = assessmentStartTime ? Date.now() - assessmentStartTime : 0;

      // Sounds variants (v3.85.0, flag on): a Sounds row records the case it
      // was given in and the letters it preset as not administered (the
      // consonants-only row: a e i o u, so totalLetters is 21 — the letters
      // presented); a vowel run records the case its cards showed. The
      // uppercase / lowercase tests carry no field: their case is the test.
      // With the flag off every doc is written exactly as before.
      const variantFields = {};
      if (variantsOn && docTestType === 'sounds') {
        variantFields.letterCase = resolvedCase;
        variantFields.notAdministered = [...runSpec.preset];
      } else if (variantsOn && isVowelTest) {
        variantFields.letterCase = resolvedCase;
      }

      await trackWrite(addDoc(collection(db, 'assessments'), {
        studentId: selectedStudent,
        classId: selectedClass,
        quarter: quarter,
        testType: docTestType,
        testCategory: TEST_CATEGORY_MAP[docTestType] || 'letter_knowledge',
        responses: responses,
        incorrectResponses: incorrectResponses,
        letterFluency: letterFluency,
        totalDurationMs: totalDurationMs,
        timestamp: new Date(),
        totalLetters: currentLetters.length,
        correctCount: Object.values(responses).filter(r => r === 'Correct').length,
        teacherId: user.uid,
        status: status,
        ...variantFields,
      }));

      await logAudit(user, 'ASSESSMENT_SUBMITTED', selectedStudent, {
        testType: docTestType,
        quarter: quarter,
        status: status,
        ...(variantFields.letterCase ? { letterCase: variantFields.letterCase, totalLetters: currentLetters.length } : {}),
      });

      const score = Object.values(responses).filter(r => r === 'Correct').length;
      if (score === currentLetters.length) {
        fireConfetti({
          particleCount: 150,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#28a745', '#007bff', '#ffc107', '#dc3545']
        });
      }

      alert('Assessment responses saved');
      // Full per-run reset (keeps class/student/test selections). Clearing
      // letterFluency/incorrectResponses/assessmentStartTime here matters:
      // uppercase-test keys ('M') don't collide with lowercase-test keys
      // ('m'), so without a reset the previous test's captured data would
      // leak into the next submission for the same student.
      resetState();
      // Now persisting the test type as requested
      // setSelectedTest('uppercase');
    } catch (err) {
      console.error('Error saving assessment:', err);
      logError(user, 'ASSESSMENT_SAVE_ERROR', err.message, err.stack, { studentId: selectedStudent });
      // Surface to the teacher — a silent failure here reads as "Submit does
      // nothing." Most likely a Firestore rules/permission issue.
      const isPermissionError = String(err?.code || err?.message || '').toLowerCase().includes('permission');
      alert(
        isPermissionError
          ? 'Could not save the assessment: permission denied. Please ask Kemal to confirm the latest Firestore rules have been deployed.'
          : `Could not save the assessment: ${err?.message || 'unknown error'}. Please check your connection and try again, and let Kemal know if it keeps happening.`
      );
    }
  };

  const resetState = () => {
    setCurrentLetterIndex(0);
    setResponses({});
    setIncorrectResponses({});
    setLetterFluency({});
    setAssessmentStartTime(null);
    setLetterStartTime(null);
    setButtonsDisabled(false);
  };

  // A different key arriving while mounted (Back / Forward between two
  // /assessment URLs) switches the sub-test exactly as the picker would:
  // fresh run, Hint closed. The picker itself stays page-local and does
  // NOT write the URL — the loader re-runs on location.search (three round
  // trips) and a navigation would drop the location.state student.
  const appliedTestRef = React.useRef(requestedTest);
  useEffect(() => {
    if (!requestedTest || appliedTestRef.current === requestedTest) return;
    appliedTestRef.current = requestedTest;
    setSelectedTest(requestedTest);
    resetState();
    setHintOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTest]);

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setSelectedStudent('');
    resetState();
    e.target.blur();
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    setSelectedStudent(studentId);
    resetState();
    e.target.blur();
  };

  const handleTestChange = (e) => {
    const key = e.target.value;
    setSelectedTest(key);
    // Page-local: the picker writes neither the URL nor the tab memory
    // (v3.84.10 rule) — the Dashboard owns those.
    resetState();
    setHintOpen(false); // a new subtest starts with the Hint closed
    e.target.blur();
  };

  // Blur after use like every other control on this page — a focused button
  // would otherwise swallow the Space/Enter keys.
  const toggleHint = (e) => {
    if (e?.target?.blur) e.target.blur();
    setHintOpen((open) => !open);
  };

  const handleSliderChange = (e) => {
    setFontSizePercentage(parseInt(e.target.value, 10));
  };

  const handleSliderEnd = async (e) => {
    // Blur the slider so keyboard shortcuts (Arrow keys) work without
    // requiring an extra click — the keydown handler ignores events while
    // an INPUT/SELECT is focused.
    if (e?.target?.blur) e.target.blur();
    if (!user) return;
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        fontSizePercentage: fontSizePercentage
      });
      if (user.profile) {
        user.profile.fontSizePercentage = fontSizePercentage;
      }
    } catch (err) {
      console.error('Failed to save font size setting:', err);
    }
  };

  // A failed load must win over the notices below — otherwise an error leaves
  // students empty and the page would claim the teacher has no roster.
  if (!loading && error) {
    return <LoadErrorNotice error={error} onRetry={retry} />;
  }

  // Calendar-driven school-year gate (v3.29.0): when no school year is active
  // per the Academic Calendar, show the notice and no page content.
  if (!loading && schoolYearInfo && schoolYearInfo.status !== 'active') {
    return <SchoolYearNotice info={schoolYearInfo} />;
  }

  // No current-school-year roster yet → guide the teacher instead of blank
  // dropdowns/tables (v3.29.0).
  if (!loading && students.length === 0) {
    return <NoRosterNotice pageLabel="the assessment page" />;
  }

  return (
    <div className="form-container" style={{ minHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', margin: '10px auto', padding: '10px', maxWidth: '100%', boxSizing: 'border-box' }}>
      <div className="assessment-banner no-print" style={{ flex: '0 0 auto', padding: '5px', marginBottom: '5px' }}>Assessment</div>
      <h2 className="page-header" style={{ flex: '0 0 auto', fontSize: '1.2em', margin: '5px 0' }}>
        Letter Recognition Assessment
      </h2>
      {loading ? (
        <p>Loading data...</p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflow: 'hidden' }}>
          {/* New Top-Row Layout for Controls */}
          <div className="controls-row" style={{ flex: '0 0 auto', display: 'flex', gap: '15px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div className="control-group" style={{ flex: '1', minWidth: '150px' }}>
              <label style={{ fontSize: '0.8em', display: 'block', marginBottom: '2px' }}>Select Class:</label>
              <select value={selectedClass} onChange={handleClassChange} style={{ padding: '4px', width: '100%' }}>
                {classes.map(classData => (
                  <option key={classData.fullId} value={classData.fullId}>{classData.originalId}</option>
                ))}
              </select>
            </div>

            <div className="control-group" style={{ flex: '1', minWidth: '150px' }}>
              <label style={{ fontSize: '0.8em', display: 'block', marginBottom: '2px' }}>Select Student:</label>
              <select value={selectedStudent} onChange={handleStudentChange} required style={{ padding: '4px', width: '100%' }}>
                <option value="">Select a student</option>
                {filteredStudents.map(student => (
                  <option key={student.id} value={student.id}>{student.name}</option>
                ))}
              </select>
            </div>

            {selectedStudent && (
              <div className="control-group" style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ fontSize: '0.8em', display: 'block', marginBottom: '2px' }}>Select Test:</label>
                {/* The row table (assessmentEntry.js): the three letter tests,
                    the Sounds variants while their flag is on, and the optional
                    vowel tests (v3.73.0) — reachable directly here; a K teacher
                    can give only these; skipping either is simply not selecting
                    it. Absent when their switch is off. */}
                <select value={selectedTest} onChange={handleTestChange} style={{ padding: '4px', width: '100%' }}>
                  {pickerRows.map((row) => (
                    <option key={row.key} value={row.key}>{row.pageLabel}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedStudent && (
              <div className="control-group" style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ fontSize: '0.8em', display: 'block', marginBottom: '2px' }}>Assessment Font:</label>
                <select value={selectedFont} onChange={(e) => { setSelectedFont(e.target.value); e.target.blur(); }} style={{ padding: '4px', width: '100%' }}>
                  <option value="Lexend">Lexend</option>
                  <option value="Mulish">Mulish</option>
                  <option value="Arial">Arial</option>
                  <option value="Comic Sans MS">Comic Sans</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Teachers">Teachers</option>
                  {variantsOn && <option value={LETTER_CARD_FONT}>{LETTER_CARD_FONT}</option>}
                </select>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {selectedStudent && (
            <div className="progress-container" style={{ flex: '0 0 auto', marginBottom: '5px' }}>
              <div className="progress-label" style={{ fontSize: '0.8em', marginBottom: '2px' }}>Question {Math.min(currentLetterIndex + 1, testLetters[selectedTest].length)} of {testLetters[selectedTest].length}</div>
              <div className="progress-bar" style={{ height: '8px' }}>
                <div
                  className="progress-fill"
                  style={{ width: `${(Math.min(currentLetterIndex, testLetters[selectedTest].length) / testLetters[selectedTest].length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {selectedStudent && (
            <div className="flashcard-container" style={{ flex: '1 1 auto', position: 'relative', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {currentLetterIndex < testLetters[selectedTest].length ? (
                  <div 
                    className="flashcard" 
                    ref={fitTextRef} 
                    style={{ 
                      width: deviceConfig.cardWidth, 
                      height: deviceConfig.cardHeight,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: '#FFF9C4', // Soft Pastel Yellow for accessibility
                      opacity: isCalculating ? 0 : 1, 
                      transition: 'opacity 0.2s ease-in',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <div className="flashcard-text" data-letter-case={resolvedCase} style={{
                      fontFamily: cardFont,
                      fontSize: `${dynamicFontSize * (fontSizePercentage / 100)}px`,
                      lineHeight: 1,
                      margin: 0,
                      padding: 0,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      transform: `translateY(${centerOffset}px)`,
                      transition: 'transform 0.2s ease-out',
                      // Both-case cards ("A a" — the vowel cards, a both-case
                      // Sounds run) must never wrap or stack (the fit-text
                      // width bound already keeps them on one line);
                      // undefined on single-glyph cards leaves their style as is.
                      whiteSpace: showsBothCases ? 'nowrap' : undefined
                    }}>
                      {currentDisplayLetter}
                    </div>
                  </div>
                ) : (
                  <div className="flashcard" style={{ width: deviceConfig.cardWidth, height: deviceConfig.cardHeight, maxWidth: '100%', maxHeight: '100%', padding: '20px', boxSizing: 'border-box' }}>
                    <div className="flashcard-text" style={{ fontSize: '2rem', color: '#28a745', pointerEvents: 'none', userSelect: 'none' }}>
                      Assessment Complete! <br /> Please hit Submit.
                    </div>
                  </div>
                )}
                
              {/* Font Size Slider */}
              <div className="slider-container no-print" style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                marginLeft: '15px', 
                height: deviceConfig.cardHeight,
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '0.8em', marginBottom: '10px', color: '#666', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Letter Size</span>
                <input 
                  type="range" 
                  min="50" 
                  max="100" 
                  step="5"
                  value={fontSizePercentage} 
                  onChange={handleSliderChange}
                  onMouseUp={handleSliderEnd}
                  onTouchEnd={handleSliderEnd}
                  style={{
                    writingMode: 'bt-lr', // For IE/Edge compatibility
                    WebkitAppearance: 'slider-vertical', // WebKit
                    appearance: 'slider-vertical',
                    width: '8px',
                    height: '60%',
                    minHeight: '150px',
                    cursor: 'pointer',
                    background: '#e0e0e0',
                    outline: 'none',
                    opacity: '0.7',
                    transition: 'opacity .2s'
                  }}
                  title="Adjust Letter Size"
                  onMouseEnter={(e) => e.target.style.opacity = '1'}
                  onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                />
                <span style={{ fontSize: '0.8em', marginTop: '10px', color: '#666' }}>{fontSizePercentage}%</span>
              </div>
            </div>
          )}

          {/* Vowel tests (v3.73.0) — teacher chrome under the card: the expected
              sound (scoring information, always shown), the "Hint" control
              holding the teacher script (off-screen by default; open state lives
              for the current subtest only — see handleTestChange), and the
              discontinue affordance — every item from this one on is recorded
              as not administered, never as incorrect. Input on these cards is
              the sibling cards' exactly: the arrow controls and Tip line below
              are shared, unguarded. */}
          {selectedStudent && isVowelTest && currentVowelItem && currentLetterIndex < testLetters[selectedTest].length && (
            <div className="no-print vowel-teacher-chrome" style={{ flex: '0 0 auto', textAlign: 'center', fontSize: '0.9em', color: '#831843', margin: '6px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: '2px 8px' }}>
                <span>Expected sound: <strong>{currentVowelItem.sound}</strong> ({currentVowelItem.example})</span>
                {/* The case the cards show, as a small tag (aa). Flag on only. */}
                {variantsOn && (
                  <span className="case-tag" data-testid="vowel-case-tag" title={caseTagTitle(resolvedCase)} style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '10px', border: '1px solid #f9a8d4', background: '#fff', color: '#9d174d', fontSize: '0.85em', fontWeight: 600, lineHeight: 1.4 }}>
                    {caseTagText(resolvedCase)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={toggleHint}
                  aria-expanded={hintOpen}
                  aria-controls="vowel-hint-panel"
                  style={{ background: 'none', border: 'none', padding: '8px 10px', minHeight: '44px', minWidth: '44px', color: '#6366f1', cursor: 'pointer', fontSize: '1em', fontWeight: 600, textDecoration: 'underline' }}
                >
                  Hint
                </button>
              </span>
              {hintOpen && (
                <div id="vowel-hint-panel" role="note" style={{ maxWidth: '560px', textAlign: 'left', background: '#fdf2f8', border: '1px solid #f9a8d4', borderRadius: '8px', padding: '8px 12px', fontSize: '0.95em', lineHeight: 1.5 }}>
                  <div><strong>Say:</strong> {VOWEL_PROMPTS[docTestType].prompt}</div>
                  <div style={{ marginTop: '4px' }}><strong>Scoring:</strong> {VOWEL_PROMPTS[docTestType].scoringNote}</div>
                  <div style={{ marginTop: '4px' }}>{VOWEL_DISCONTINUE_RULE}</div>
                </div>
              )}
              <button
                type="button"
                onClick={handleMarkRestNotAdministered}
                disabled={buttonsDisabled}
                style={{ padding: '10px 14px', minHeight: '44px', borderRadius: '6px', border: '1px solid #f9a8d4', background: '#fff', color: '#9d174d', cursor: 'pointer', fontSize: '0.95em', fontWeight: 600 }}
              >
                Mark the rest not administered
              </button>
            </div>
          )}

          {selectedStudent && (
            <AssessmentResponseControls
              onBack={handleBack}
              onIncorrect={() => handleResponse(false)}
              onCorrect={() => handleResponse(true)}
              backDisabled={currentLetterIndex === 0}
              responseDisabled={buttonsDisabled || currentLetterIndex >= testLetters[selectedTest].length}
              itemLabel="letter"
            />
          )}

          {selectedStudent && currentLetterIndex < testLetters[selectedTest].length && (
            <div className="no-print" style={{ flex: '0 0 auto', textAlign: 'center', fontSize: '0.85em', color: '#64748b', marginTop: '-8px', paddingBottom: '6px' }}>
              Tip: press the letter key the student actually said — matching the shown letter counts as correct; any other letter is marked incorrect and their answer is recorded.
            </div>
          )}

          {selectedStudent && (
            <div className="progress-text" style={{ flex: '0 0 auto', paddingBottom: '10px', textAlign: 'center' }}>
              {Object.keys(responses).length === testLetters[selectedTest].length && currentLetterIndex >= testLetters[selectedTest].length ? (
                <button type="submit" className="submit-button" style={{ background: 'linear-gradient(45deg, #28a745, #20c997)', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
                  Submit Assessment
                </button>
              ) : null}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

export default AssessmentForm;
